import React from 'react';
import { Calendar, Clock, DollarSign, Users, Zap, ArrowRight, FileText, AlertTriangle, Loader2 } from 'lucide-react';
import {
  useListSiteContractsQuery,
  useGetContractTermsQuery,
  useGetContractKeyDatesQuery,
  useGetContractKeyTermsQuery,
  useGetContractClausesQuery,
  useGetContractPartiesQuery,
} from '../../redux/api/contractsApi';
import { useAppSelector } from '../../redux/hooks';

interface ContractTermsPanelProps {
  documentId: string;
  onPageClick?: (page: number) => void;
}

const PageLink: React.FC<{ page: number | null; onClick?: (page: number) => void }> = ({ page, onClick }) => {
  if (page == null) return null;
  return (
    <button
      onClick={() => onClick?.(page)}
      className="text-xs font-mono text-engramiq-green hover:underline whitespace-nowrap"
    >
      [pg {page} &rarr;]
    </button>
  );
};

const ContractTermsPanel: React.FC<ContractTermsPanelProps> = ({ documentId, onPageClick }) => {
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const siteId = currentSite?.site_id || '';

  // Find contracts linked to this document by listing site contracts
  // and filtering client-side (the API filters by site, we match by document_id)
  const { data: contractsList, isLoading: contractsLoading } = useListSiteContractsQuery(
    { siteId, limit: 100 },
    { skip: !siteId },
  );

  const contract = contractsList?.items.find((c) => c.document_id === documentId);
  const contractId = contract?.id || '';
  const hasContract = !!contractId;

  const { data: termsData } = useGetContractTermsQuery({ contractId }, { skip: !hasContract });
  const { data: datesData } = useGetContractKeyDatesQuery({ contractId }, { skip: !hasContract });
  const { data: keyTermsData } = useGetContractKeyTermsQuery({ contractId }, { skip: !hasContract });
  const { data: clausesData } = useGetContractClausesQuery({ contractId }, { skip: !hasContract });
  const { data: partiesData } = useGetContractPartiesQuery({ contractId }, { skip: !hasContract });

  if (contractsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-engramiq-green" />
        <span className="ml-2 text-xs eg-text-secondary">Loading contract data...</span>
      </div>
    );
  }

  if (!hasContract) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <FileText className="w-8 h-8 mb-2" style={{ color: 'var(--color-text-muted)' }} />
        <p className="text-sm eg-text-secondary mb-1">No contract terms extracted</p>
        <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          This document hasn't been processed through the contract extraction pipeline yet.
        </p>
      </div>
    );
  }

  const terms = termsData?.items || [];
  const dates = datesData?.items || [];
  const keyTerms = keyTermsData?.items || [];
  const clauses = clausesData?.items || [];
  const parties = partiesData?.items || [];

  return (
    <div className="space-y-4 p-4 overflow-y-auto max-h-full">
      {/* Contract Header */}
      {contract && (
        <div className="eg-card">
          <div className="eg-card-body">
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-0.5 rounded text-[10px] font-semibold text-white bg-blue-500 uppercase">
                {contract.contract_type}
              </span>
              <span className="px-2 py-0.5 rounded text-[10px] font-medium"
                style={{
                  color: contract.status === 'active' ? '#10b981' : '#9ca3af',
                  border: `1px solid ${contract.status === 'active' ? '#10b981' : '#9ca3af'}`,
                }}>
                {contract.status}
              </span>
            </div>
            <h3 className="text-sm font-semibold eg-text-primary">{contract.title}</h3>
          </div>
        </div>
      )}

      {/* Key Dates */}
      {dates.length > 0 && (
        <div className="eg-card">
          <div className="eg-card-header flex items-center gap-2">
            <Calendar className="w-4 h-4 text-engramiq-green" />
            <h3 className="text-sm font-semibold eg-text-primary">Key Dates</h3>
            <span className="text-[10px] eg-text-secondary">({dates.length})</span>
          </div>
          <div className="eg-card-body space-y-2">
            {dates.map((item) => (
              <div key={item.id} className="flex items-center justify-between py-1">
                <div className="flex items-center gap-2">
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-blue-500/20 text-blue-400 uppercase">
                    {item.date_type}
                  </span>
                  <span className="text-xs eg-text-secondary">{item.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium eg-text-primary">
                    {new Date(item.date_value + 'T00:00:00').toLocaleDateString()}
                  </span>
                  <PageLink page={item.source_page} onClick={onPageClick} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Terms (fees, rates, guarantees) */}
      {keyTerms.length > 0 && (
        <div className="eg-card">
          <div className="eg-card-header flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-engramiq-green" />
            <h3 className="text-sm font-semibold eg-text-primary">Key Terms</h3>
            <span className="text-[10px] eg-text-secondary">({keyTerms.length})</span>
          </div>
          <div className="eg-card-body space-y-2">
            {keyTerms.map((item) => {
              const value = item.value_number != null
                ? `${item.currency || ''}${item.value_number.toLocaleString()}${item.unit ? ` ${item.unit}` : ''}`
                : item.value_text || '-';
              return (
                <div key={item.id} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-green-500/20 text-green-400 uppercase">
                      {item.term_type}
                    </span>
                    <span className="text-xs eg-text-secondary">{item.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium eg-text-primary">{value}</span>
                    <PageLink page={item.source_page} onClick={onPageClick} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Extracted Terms (all fields) */}
      {terms.length > 0 && (
        <div className="eg-card">
          <div className="eg-card-header flex items-center gap-2">
            <FileText className="w-4 h-4 text-engramiq-green" />
            <h3 className="text-sm font-semibold eg-text-primary">All Extracted Fields</h3>
            <span className="text-[10px] eg-text-secondary">({terms.length})</span>
          </div>
          <div className="eg-card-body space-y-2">
            {terms.map((term) => {
              const value = term.value_text || (term.value_number != null ? String(term.value_number) : term.value_date || '-');
              return (
                <div key={term.id} className="flex items-center justify-between py-1">
                  <span className="text-xs eg-text-secondary">
                    {term.field_label}
                    {term.needs_review && <AlertTriangle className="inline w-3 h-3 ml-1 text-yellow-500" />}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium eg-text-primary max-w-[200px] truncate">{value}</span>
                    {term.confidence != null && (
                      <span className={`text-[10px] font-mono ${term.confidence >= 0.8 ? 'text-green-400' : 'text-yellow-400'}`}>
                        {(term.confidence * 100).toFixed(0)}%
                      </span>
                    )}
                    <PageLink page={term.source_page} onClick={onPageClick} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* IFTTT Clauses */}
      {clauses.length > 0 && (
        <div className="eg-card">
          <div className="eg-card-header flex items-center gap-2">
            <ArrowRight className="w-4 h-4 text-engramiq-green" />
            <h3 className="text-sm font-semibold eg-text-primary">IFTTT Clauses</h3>
            <span className="text-[10px] eg-text-secondary">({clauses.length})</span>
          </div>
          <div className="eg-card-body space-y-3">
            {clauses.map((clause) => (
              <div
                key={clause.id}
                className="rounded-lg p-3"
                style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-purple-500/20 text-purple-400 uppercase">
                        {clause.clause_type.replace(/_/g, ' ')}
                      </span>
                      <span className="text-xs font-medium eg-text-primary">{clause.label}</span>
                    </div>
                    {clause.condition && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-engramiq-green">IF</span>
                        <span className="text-xs eg-text-primary">{clause.condition}</span>
                      </div>
                    )}
                    {clause.consequence && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>THEN</span>
                        <span className="text-xs eg-text-secondary">{clause.consequence}</span>
                      </div>
                    )}
                  </div>
                  <PageLink page={clause.source_page} onClick={onPageClick} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Parties */}
      {parties.length > 0 && (
        <div className="eg-card">
          <div className="eg-card-header flex items-center gap-2">
            <Users className="w-4 h-4 text-engramiq-green" />
            <h3 className="text-sm font-semibold eg-text-primary">Parties</h3>
          </div>
          <div className="eg-card-body space-y-2">
            {parties.map((party) => (
              <div key={party.id} className="flex items-center justify-between py-1">
                <div>
                  <span className="text-[10px] uppercase eg-text-secondary">{party.role}</span>
                  <p className="text-xs font-medium eg-text-primary">{party.name}</p>
                </div>
                <PageLink page={party.source_page} onClick={onPageClick} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractTermsPanel;
